import classes from './_validation_message.module.scss';
import { testClass } from 'music';

/** @typedef {import('./types.js').ValidationState} ValidationState */

/**
 * Convert a camelCase string to dash-case.
 * The code is identical to music's camelToSnake utility,
 * except that '_' is replaced by '-'.
 *
 * @param {String} str - The CamelCase string to convert
 * @return {String} - the dash-case version of `str`.
 */
function camelToKebab(str) {
  return str.replace(/[\w]([A-Z])/g, function(m) {
    return m[0] + '-' + m[1];
  }).toLowerCase();
}

export default class UaValidationMessage extends HTMLElement {
  /**
   * @type {'minlength'|'required'|'must-contain-number'|'username-unique'|
   *        'email-unique'|'passwords-must-match'} Validation type.
   */
  validationType;

  /** @type {ValidationState} State of the validation message. */
  validationState;

  /** @type {number|string} Comparison value (for, e.g., minlength).  */
  validationValue;

  /**
   * Get the current validation state.
   * @return {ValidationState}
   */
  get state() {
    return this.validationState;
  }

  /**
   * Set the state of the validation message.
   * @param {ValidationState} newState
   */
  set state(newState) {
    if (['neutral', 'error', 'success'].includes(newState)) {
      this.validationState = newState;
      this.update();
    } else throw new Error('UaValidationMessage error: Invalid state.');
  }

  /**
   * Sets validity and transforms the validity into one of the allowed states.
   * @param {boolean} validity
   */
  set isValid(validity) {
    const newState = validity === true ? 'success' : 'error';
    this.state = newState;
  }

  /**
   * Template for inner markup.
   * @return {string} HTML string.
   */
  get template() {
    return `
      <${this.checkIconSelector}
        role="presentation"
        label=""
        size="24"
        class="${classes['validation-icon']}  ${classes['validation-icon--check']}">
      </${this.checkIconSelector}>
      <music-icon-x-v3
        role="presentation"
        label=""
        size="24"
        class="${classes['validation-icon']}  ${classes['validation-icon--x']}">
      </music-icon-x-v3>
      <span class="${classes['validation-message__text']}">
        ${this.message}
      </span>
    `;
  }

    /**
   * Selector for check icon markup.
   * @return {string} HTML string.
   */
  get checkIconSelector() {
    if (this.state === 'neutral') {
      return 'music-icon-check-v3'
    } else {
      return 'music-icon-check-circle-v3';
    }
  }

  /**
   * Render the message with the current state.
   */
  update() {
    this.innerHTML = this.template;

    switch(this.validationState) {
      case 'error':
        this.classList.add(classes['has-error'])
        this.classList.remove(classes['has-success'])
        break;
      case 'success':
        this.classList.add(classes['has-success']);
        this.classList.remove(classes['has-error']);
        break;
      case 'neutral':
        this.classList.remove(classes['has-success']);
        this.classList.remove(classes['has-error']);
        break;
    }
  }

  /**
   * Initialize and render the validation message.
   */
  connectedCallback() {
    this.update();

    const fieldNameKebabCase = this.fieldName.replaceAll('_', '-');
    const validationMessageTestClass = testClass(
      `${fieldNameKebabCase}--${camelToKebab(this.validationType)}`
    );
    const classesToAdd = [
      classes['validation-message'],
      classes[`validation-message--${camelToKebab(this.validationType)}`],
      validationMessageTestClass,
    ].join('  ');
    this.className = `${this.className}  ${classesToAdd}`;
  }
}
